import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaBars, FaTimes, FaUser, FaSignOutAlt } from 'react-icons/fa';

const Nav = styled.nav`
  background: white;
  height: ${props => props.theme.sizes.headerHeight};
  display: flex;
  justify-content: center;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999;
  box-shadow: ${props => props.theme.shadows.small};
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const NavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: ${props => props.theme.sizes.maxWidth};
  padding: 0 30px;
`;

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  padding: 10px 0;

  img {
    height: 65px;
    width: auto;
    object-fit: contain;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: ${props => props.theme.colors.primary};
  transition: color 0.3s ease;

  &:hover {
    color: ${props => props.theme.colors.hover};
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: block;
  }
`;

const NavLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    display: ${props => (props.isOpen ? 'flex' : 'none')};
    flex-direction: column;
    position: absolute;
    top: ${props => props.theme.sizes.headerHeight};
    left: 0;
    right: 0;
    background: white;
    padding: 25px;
    box-shadow: ${props => props.theme.shadows.medium};
    gap: 25px;
    border-bottom: 1px solid ${props => props.theme.colors.border};
  }
`;

const NavLink = styled(Link)`
  color: ${props => props.theme.colors.text};
  text-decoration: none;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.3s ease;
  padding: 8px 0;
  position: relative;

  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: ${props => props.theme.colors.primary};
    transition: width 0.3s ease;
    border-radius: 2px;
  }

  &:hover {
    color: ${props => props.theme.colors.primary};
    
    &:after {
      width: 100%;
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    width: 100%;
    text-align: center;
    padding: 12px 0;
    font-size: 18px;
    
    &:after {
      bottom: 6px;
    }
  }
`;

const AdminButton = styled.button`
  background: ${props => props.theme.colors.primary};
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.theme.colors.hover};
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    margin-top: 10px;
    width: 100%;
    justify-content: center;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 30px;
  border-radius: 15px;
  width: 90%;
  max-width: 400px;
  box-shadow: ${props => props.theme.shadows.large};

  h3 {
    color: ${props => props.theme.colors.primary};
    margin-bottom: 20px;
    text-align: center;
  }
`;

const LoginForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 15px;

  input {
    padding: 12px;
    border: 2px solid ${props => props.theme.colors.border};
    border-radius: 8px;
    font-size: 16px;

    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.primary};
    }
  }

  button {
    background: ${props => props.theme.colors.primary};
    color: white;
    border: none;
    padding: 12px;
    border-radius: 8px;
    font-size: 16px;
    cursor: pointer;
    transition: background 0.3s;

    &:hover {
      background: ${props => props.theme.colors.hover};
    }
  }
`;

const ErrorMessage = styled.p`
  color: ${props => props.theme.colors.error};
  font-size: 14px;
  text-align: center;
  margin-top: 10px;
`;

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loginData, setLoginData] = useState({ id: '', password: '' });
  const [loginError, setLoginError] = useState('');

  useEffect(() => {
    // 페이지 로드 시 localStorage에서 로그인 상태 확인
    const adminStatus = localStorage.getItem('isAdmin');
    if (adminStatus === 'true') {
      setIsAdmin(true);
    }
  }, []);

  const handleLinkClick = () => {
    setIsOpen(false);
    // 페이지 맨 꼭대기로 부드럽게 스크롤
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleLogin = (e) => {
    e.preventDefault();
    
    if (loginData.id === 'admin' && loginData.password === 'junior2024') {
      setIsAdmin(true);
      localStorage.setItem('isAdmin', 'true');
      setShowLoginModal(false);
      setLoginData({ id: '', password: '' });
      setLoginError('');
    } else {
      setLoginError('아이디 또는 비밀번호가 올바르지 않습니다.');
    }
  };

  const handleLogout = () => {
    setIsAdmin(false);
    localStorage.removeItem('isAdmin');
  };

  const closeModal = () => {
    setShowLoginModal(false);
    setLoginData({ id: '', password: '' });
    setLoginError('');
  };

  return (
    <>
      <Nav>
        <NavContainer>
          <LogoLink to="/">
            <img src="/logo.png" alt="서울쥬니어치과 로고" />
          </LogoLink>

          <MenuButton onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <FaTimes /> : <FaBars />}
          </MenuButton>

          <NavLinks isOpen={isOpen}>
            <NavLink to="/staff" onClick={handleLinkClick}>의료진 소개</NavLink>
            <NavLink to="/gallery" onClick={handleLinkClick}>치과 둘러보기</NavLink>
            <NavLink to="/blog" onClick={handleLinkClick}>치아관리정보</NavLink>
            <NavLink to="/pricing" onClick={handleLinkClick}>비급여수가표</NavLink>
            <NavLink to="/location" onClick={handleLinkClick}>오시는 길</NavLink>
            <NavLink to="/notice" onClick={handleLinkClick}>공지사항</NavLink>
            
            {isAdmin ? (
              <AdminButton onClick={handleLogout}>
                <FaSignOutAlt />
                로그아웃
              </AdminButton>
            ) : (
              <AdminButton onClick={() => setShowLoginModal(true)}>
                <FaUser />
                관리자
              </AdminButton>
            )}
          </NavLinks>
        </NavContainer>
      </Nav>

      {showLoginModal && (
        <Modal onClick={closeModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <h3>관리자 로그인</h3>
            <LoginForm onSubmit={handleLogin}>
              <input
                type="text"
                placeholder="아이디"
                value={loginData.id}
                onChange={(e) => setLoginData({...loginData, id: e.target.value})}
                required
              />
              <input
                type="password"
                placeholder="비밀번호"
                value={loginData.password}
                onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                required
              />
              <button type="submit">로그인</button>
              {loginError && <ErrorMessage>{loginError}</ErrorMessage>}
            </LoginForm>
          </ModalContent>
        </Modal>
      )}
    </>
  );
}

export default Navbar; 